import {
  ErrorText, Text, Link
} from '@comicrelief/component-library';
import { useCartContext } from '@/services/cart/CartContext';
import { useAppSelector } from '@/store/hooks';
import { useValidateTransactionComplete } from './useValidateTransactionComplete';
import { usePreventBackNavigation } from '@/services/customHooks/usePreventBackNavigation';
import PageHeaderSimplified from '@/components/pageHeader/PageHeaderSimplified';
import { getCurrentJourney, formatAmount } from '@/services/journeys/utils';

import SocialLinks from './SocialLinks';
import { FULFILMENT_TYPES } from '@/constants/constants';
import { ThankYouCopy } from './ThankYouCopy';
import {
  StyledTransaction,
  Box,
  StyledButtonWrapper,
  ThankYouCopyWrapper
} from './PaymentStyles.style';

interface PaymentSubmittedProps {
  currencySymbol: string;
  totalSteps: number;
}

function PaymentSubmitted({ totalSteps, currencySymbol }: PaymentSubmittedProps) {
  const {
    cartData, getCartJourneyCustomSuccessMessage, getCartFulfilmentType, getCartJourneySocialShare
  } = useCartContext();
  let hideSocial: boolean | undefined = false;
  const isAARN = getCartFulfilmentType() === FULFILMENT_TYPES.AARN;
  const transactionState = useAppSelector(state => state.transaction);
  const userDetailsState = useAppSelector(state => state.userDetails);
  const journeysState = useAppSelector(state => state.journeys);
  const shippingDetailsState = useAppSelector(state => state.shippingDetails);
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const journeyType = useAppSelector(state => state.journeys?.journey);

  // Validate state is as expected, otherwise redirect to index
  useValidateTransactionComplete();

  // Prevent back navigation to marketing prefs page, instead go to the homepage
  usePreventBackNavigation();

  const thisJourneyName = getCurrentJourney(journeysState);
  const { firstName } = userDetailsState;
  const thisCustomSuccessMsg = getCartJourneyCustomSuccessMessage();

  const successButtonText = cartData?.customSuccessButton?.text?.trim()
    ? cartData.customSuccessButton.text
    : ThankYouCopy.defaultSuccessButtonText;

  const successButtonUrl = cartData?.customSuccessButton?.linkUrl?.trim()
    ? cartData.customSuccessButton.linkUrl
    : ThankYouCopy.defaultSuccessButtonUrl;

  const aarnThankYouCopy = (optedIn:boolean) => (
    <ThankYouCopyWrapper>
      <Text tag="p" weight="bold">
        {optedIn ? ThankYouCopy.aarnOptedInHeader :  ThankYouCopy.aarnOptedOutHeader}
      </Text>

      <Text tag="p">
        {optedIn ? ThankYouCopy.aarnOptedInCopy :  ThankYouCopy.aarnOptedOutCopy}
      </Text>

      <Text tag="p">
        <Link color="white" target="blank" href={ThankYouCopy.ctaLink} type="button">
          {ThankYouCopy.ctaCopy}
        </Link>
      </Text>

    </ThankYouCopyWrapper>
  );

  // Typechecking for the 2 GivingTypes/Journeys that feature this option:
  if (thisJourneyName === 'single' || thisJourneyName === 'monthly') {
    hideSocial = Boolean(cartData?.journeys[thisJourneyName]?.socialShare?.isHidden);
  }

  const socialShareConfig = getCartJourneySocialShare();
  const socialShareCTA = socialShareConfig?.cta ?? 'Spread the word:';
  const cartId = cartData?.id;

  return (
    <>
      <PageHeaderSimplified
        // TO DO: This line below needs sorting in another PR, we should not have that hardcoded
        currentStep={11}
        totalSteps={totalSteps}
        headerText={firstName ? `Thank you, ${firstName}` : 'Thank you'}
        initialText="You just did something amazing!"
        data-test="paymentSubmittedPageHeader"
      />

      {transactionState?.errorMessage
        ? <ErrorText>{transactionState?.errorMessage}</ErrorText>
        : null}

      <StyledTransaction tag="p" family="Montserrat" color="black" size="xs" data-test="paymentSubmittedTransactionId">
        {!transactionState.subscriptionId ? 'Your transaction id:' : 'Your subscription id:'}
        {' '}
        {!transactionState.subscriptionId ? transactionState.transactionId : transactionState.subscriptionId}
      </StyledTransaction>

      <Box data-test="success-copy">
        {/* For Adopt A Red Nose, we use hardcoded copy to allow the full markup the pre-existing plain text
        Custom Succes Message doesn't allow for. We're also switching AARN copy based on the 'opt' status */}
        { isAARN
          ? (aarnThankYouCopy(shippingDetailsState.postalRewards))
          : thisCustomSuccessMsg ? (
            <>
              <Text tag="p" family="Montserrat" size="md" color="black" weight="bold">
                Your
                {' '}
                {currencySymbol}
                {formatAmount(paymentSelectState.amount)}
                {' '}
                goes a long way.
              </Text>
              <Text tag="p" family="Montserrat" color="black">
                { thisCustomSuccessMsg }
              </Text>
            </>
          ) : (
            <>
              <Text tag="p" family="Montserrat" size="md" color="black">
                You have helped people in the UK and around the world with your donation today.
              </Text>
              <Text tag="p" family="Montserrat" size="md" color="black">
                From mental health support to providing a safe place to live, you are there for people in need. Thank you.
              </Text>
            </>
          )}

        { !hideSocial && (
          <SocialLinks
            socialShareCTA={socialShareCTA}
            currencySymbol={currencySymbol}
            cartId={cartId}
            paymentAmount={paymentSelectState.amount}
            journeyType={journeyType}
            prepopulatedSocialShareMessage={socialShareConfig?.message}
          />
        )}

      </Box>

      <StyledButtonWrapper>
        <Link
          color="red"
          href={successButtonUrl}
          target="self"
          type="button"
        >
          {successButtonText}
        </Link>
      </StyledButtonWrapper>
    </>
  );
}

export default PaymentSubmitted;
